import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { PageHero } from '@/components/PageHero';
import { NexusMark } from '@/components/NexusMark';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

const assets = [
  { label: 'Nexus mark (icon only)', href: '/brand/nexus-mark.svg' },
  { label: 'Nexus lockup (mark + wordmark)', href: '/brand/nexus-lockup.svg' },
];

export function Press(): JSX.Element {
  useSeo({
    title: 'Press & brand assets',
    description:
      'Company boilerplate and brand assets for Nexus, built by Elemental TS. For media inquiries or anything not covered here, email hello@nexusrmm.ai.',
    path: '/press',
  });

  return (
    <div>
      <PageHero
        eyebrow="Press"
        title="Boilerplate and brand assets."
        subtitle="Everything below is exactly what it says — no press mentions to list yet, because we'd rather have an empty section than an invented one."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">Boilerplate</h2>
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <p className="text-[15px] leading-relaxed text-white/75">
                Nexus is an agentic PSA + RMM platform built by Elemental TS, an MSP that built the
                platform after running its own client operations across a stitched-together stack
                of separate tools. Nexus consolidates ticketing, endpoint management, Microsoft 365
                backup, security, CRM, unified tenant management, and compliance tracking into one
                platform — live in Elemental TS&apos;s own MSP practice today, opening to a limited
                design-partner cohort of other MSPs.
              </p>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <h2 className="mt-12 font-display text-2xl font-bold text-nexus-ink md:text-3xl">Brand assets</h2>
            <p className="mt-3 text-sm text-white/60">
              SVG, vector, background-agnostic. Need a different format? Email us and we&apos;ll send it.
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {assets.map((a) => (
                <a
                  key={a.href}
                  href={a.href}
                  download
                  className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
                      <NexusMark size={28} />
                    </div>
                    <span className="text-sm font-semibold text-nexus-ink">{a.label}</span>
                  </div>
                  <DownloadOutlinedIcon fontSize="small" className="flex-shrink-0 text-white/40" />
                </a>
              ))}
            </div>
            <p className="mt-4 text-xs text-white/40">
              Please don&apos;t alter the mark&apos;s proportions or colors, or use it to imply an
              endorsement or partnership that doesn&apos;t exist.
            </p>
          </Reveal>

          <Reveal delay={140}>
            <h2 className="mt-12 font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              Media inquiries
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-white/75">
              Email{' '}
              <a href="mailto:hello@nexusrmm.ai" className="text-nexus-purple-light no-underline hover:underline">
                hello@nexusrmm.ai
              </a>{' '}
              — it reaches the team directly, no press office to route through.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
